// Every terraform job's module inventory, rolled up fleet-wide. There is no
// fleet-wide stack action yet, so this asks getStackModules per job from the
// jobs catalog, a few at a time, and aggregates whatever has arrived. Once
// every job has answered it asks getModuleVersions (the Spacelift registry)
// for each module's latest release.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useDeploymentData } from './useDeploymentData';
import { stackModulesKey } from '../lib/stackModules';
import { buildFleetModules, FleetModules, JobStacks } from '../lib/fleetModules';

/** getStackModules requests in flight at once. */
export const FLEET_CONCURRENCY = 6;
/** A request with no reply after this long is sent again. */
const STALE_MS = 20_000;
/** getModuleVersions' per-request cap (maxSources in the Lambda). */
const VERSIONS_BATCH = 300;

export interface FleetModulesState {
  fleet: FleetModules;
  /** Terraform jobs answered / in the catalog. */
  progress: { loaded: number; total: number };
  /** True until the catalog and every job have answered. */
  loading: boolean;
  errors: Array<{ app: string; jobId: string; error: string }>;
  /** Spacelift lookups: still running, and the error that stopped them, if any. */
  registry: { loading: boolean; error?: string };
}

export const useFleetModules = (): FleetModulesState => {
  const {
    jobs,
    jobsLoaded,
    jobsLoading,
    fetchJobs,
    wsStatus,
    stackModules,
    fetchStackModules,
    moduleVersions,
    moduleVersionsPending,
    moduleVersionsError,
    fetchModuleVersions,
  } = useDeploymentData();
  const sentAt = useRef<Map<string, number>>(new Map());
  const versionsAsked = useRef<Set<string>>(new Set());
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (wsStatus === 'connected' && !jobsLoaded && !jobsLoading) fetchJobs();
  }, [wsStatus, jobsLoaded, jobsLoading, fetchJobs]);

  const tfJobs = useMemo(() => jobs.filter(j => j.type === 'terraform'), [jobs]);

  // Keep FLEET_CONCURRENCY requests in flight until every job has answered.
  useEffect(() => {
    if (wsStatus !== 'connected') return;
    const now = Date.now();
    let inflight = 0;
    const queue: Array<{ app: string; jobId: string; key: string }> = [];
    tfJobs.forEach(j => {
      const key = stackModulesKey(j.app_name, j.job_id);
      const slice = stackModules.get(key);
      if (slice?.loaded && !slice.loading) return;
      const sent = sentAt.current.get(key);
      if (slice?.loading && sent !== undefined && now - sent < STALE_MS) {
        inflight += 1;
        return;
      }
      queue.push({ app: j.app_name, jobId: j.job_id, key });
    });
    queue.slice(0, Math.max(0, FLEET_CONCURRENCY - inflight)).forEach(({ app, jobId, key }) => {
      sentAt.current.set(key, now);
      fetchStackModules(app, jobId);
    });
  }, [wsStatus, tfJobs, stackModules, fetchStackModules, tick]);

  const loadedJobs = useMemo(
    () =>
      tfJobs.filter(j => {
        const s = stackModules.get(stackModulesKey(j.app_name, j.job_id));
        return !!s?.loaded;
      }),
    [tfJobs, stackModules]
  );
  const done = jobsLoaded && loadedJobs.length >= tfJobs.length;

  // Re-check for stale requests while anything is outstanding.
  useEffect(() => {
    if (done) return;
    const id = window.setInterval(() => setTick(t => t + 1), 5_000);
    return () => window.clearInterval(id);
  }, [done]);

  const input: JobStacks[] = useMemo(
    () =>
      loadedJobs.map(j => ({
        app: j.app_name,
        jobId: j.job_id,
        stacks: stackModules.get(stackModulesKey(j.app_name, j.job_id))?.stacks || [],
      })),
    [loadedJobs, stackModules]
  );
  const fleet = useMemo(() => buildFleetModules(input, moduleVersions), [input, moduleVersions]);

  // Once the inventory is complete, ask the registry about every module not
  // yet asked about. A lost connection forgets what was asked.
  useEffect(() => {
    if (wsStatus !== 'connected') {
      versionsAsked.current.clear();
      return;
    }
    if (!done) return;
    const missing = fleet.modules
      .map(m => m.source)
      .filter(src => !moduleVersions.has(src) && !versionsAsked.current.has(src));
    for (let i = 0; i < missing.length; i += VERSIONS_BATCH) {
      const batch = missing.slice(i, i + VERSIONS_BATCH);
      batch.forEach(src => versionsAsked.current.add(src));
      fetchModuleVersions(batch);
    }
  }, [wsStatus, done, fleet.modules, moduleVersions, fetchModuleVersions]);

  const errors = useMemo(
    () =>
      loadedJobs.flatMap(j => {
        const error = stackModules.get(stackModulesKey(j.app_name, j.job_id))?.error;
        return error ? [{ app: j.app_name, jobId: j.job_id, error }] : [];
      }),
    [loadedJobs, stackModules]
  );

  return {
    fleet,
    progress: { loaded: loadedJobs.length, total: tfJobs.length },
    loading: !done,
    errors,
    registry: { loading: moduleVersionsPending > 0, error: moduleVersionsError },
  };
};
